import type { ModalTriggerProps } from '@ztechapps/webui.modal'
import { Modal } from '@ztechapps/webui.modal'
import { InventoryBySerialNumberStatusEnum } from '@ztechapps/common.contract'
import { HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { SaveCancelButtons } from '@ztechapps/webui.button'
import { InventoryBySerialNumberModalTrigger } from './InventoryBySerialNumberModalTrigger'
import { useReactiveState } from '@ztechapps/common.hook'
import type { InventoryBySerialNumberValue } from './InventoryBySerialNumberInput'
import { InventoryBySerialNumberCodeInput } from './Code/inventoryBySerialNumberCodeInput'
import { SerialNumberSelectList } from '../SerialNumberSelectList'

export type InventoryBySerialNumberModalProps = {
  isBusy?: boolean
  value: InventoryBySerialNumberValue
  onChange: (value: InventoryBySerialNumberValue) => Promise<void>
  disabled?: boolean
}
export function InventoryBySerialNumberModal({ value, disabled, isBusy, onChange }: InventoryBySerialNumberModalProps) {
  const [ibsnValue, setIbsnValue] = useReactiveState<InventoryBySerialNumberValue>(value)
  const { product, quantity } = ibsnValue
  const inventoryBySerialNumbers = ibsnValue.inventoryBySerialNumbers ?? []

  const isPositiveCount = quantity > 0
  const positiveQuantity = Math.abs(quantity)

  const saveDisabled = disabled || inventoryBySerialNumbers.length !== positiveQuantity

  const modalTrigger = (triggerProps: ModalTriggerProps) => (
    <InventoryBySerialNumberModalTrigger
      {...triggerProps}
      inventoryBySerialNumbers={inventoryBySerialNumbers}
      disabled={disabled}
    />
  )

  return (
    <Modal centered size={isPositiveCount ? 'md' : 'lg'} trigger={modalTrigger}>
      {({ toggleShowModal }) => {
        return (
          <>
            <Modal.DragHandle>
              {isPositiveCount ? 'Add serial numbers' : 'Select serial numbers to de-activate'}
            </Modal.DragHandle>
            <Modal.Body>
              <ShowIf condition={isPositiveCount}>
                <InventoryBySerialNumberCodeInput
                  value={inventoryBySerialNumbers}
                  onChange={(value) => {
                    setIbsnValue({ ...ibsnValue, inventoryBySerialNumbers: value })
                  }}
                  maxQtySerialNumber={quantity}
                  product={product}
                />
              </ShowIf>
              <ShowIf condition={!isPositiveCount}>
                <SerialNumberSelectList
                  filter={{
                    'product._id': product?._id,
                    status: InventoryBySerialNumberStatusEnum.OnHand,
                  }}
                  onSelectionChange={(selectedItems) => {
                    const mappedItems = selectedItems.map((item) => ({
                      ...item,
                      value: item._id,
                      label: item.code,
                    }))
                    setIbsnValue({ ...ibsnValue, inventoryBySerialNumbers: mappedItems })
                  }}
                  initialSelection={inventoryBySerialNumbers}
                  requiredQty={positiveQuantity}
                  maxSelection={positiveQuantity}
                  disabled={saveDisabled}
                />
              </ShowIf>
            </Modal.Body>
            <Modal.Footer>
              <HorizontalStack className={'justify-content-end'}>
                <SaveCancelButtons
                  isBusy={isBusy}
                  disabled={saveDisabled}
                  saveText={'Set Serial Numbers'}
                  saveTitle={'Set serial numbers'}
                  onSave={async () => {
                    await onChange(ibsnValue)
                    toggleShowModal()
                  }}
                  onCancel={async () => {
                    //we just need to clear the data
                    await onChange(value)
                    setIbsnValue(value)
                    toggleShowModal()
                  }}
                />
              </HorizontalStack>
            </Modal.Footer>
          </>
        )
      }}
    </Modal>
  )
}
